/**
 * Admin CMS Pages Manager - Olympic Fit Wellness
 * Visual drag-and-drop page builder integrated
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Save,
  X,
  AlertCircle,
  Check,
  Loader2,
  Layout
} from 'lucide-react';
import api from '../../lib/api';
import VisualCMSEditor from '../../components/VisualCMSEditor';

export default function CMSPagesPage() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPage, setEditingPage] = useState(null);
  const [visualEditorPage, setVisualEditorPage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPage, setNewPage] = useState({ title: '', slug: '' });

  const loadPages = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getCMSPages();
      setPages(data || []);
    } catch (err) {
      console.error('Error loading pages:', err);
      setPages(defaultPages);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const defaultPages = [
    { page_id: 'page_001', slug: 'home', title: 'Home Page', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_002', slug: 'about', title: 'About Us', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_003', slug: 'services', title: 'Services', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_004', slug: 'membership', title: 'Membership', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_005', slug: 'locations', title: 'Locations', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_006', slug: 'trainers', title: 'Trainers', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } },
    { page_id: 'page_007', slug: 'contact', title: 'Contact', status: 'published', updated_at: new Date().toISOString(), content: { blocks: [] } }
  ];

  const displayPages = pages.length > 0 ? pages : defaultPages;

  const filteredPages = displayPages.filter(page => 
    page.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const savePage = async () => {
    if (!editingPage) return;
    setIsSaving(true);
    setError(null);
    try {
      await api.updateCMSPage(editingPage.slug, {
        content: editingPage.content,
        featured_image: editingPage.featured_image
      });
      setSuccess('Page saved successfully!');
      setEditingPage(null);
      loadPages();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  const saveVisualEditor = async (blocks) => {
    if (!visualEditorPage) return;
    setIsSaving(true);
    try {
      await api.updateCMSPage(visualEditorPage.slug, {
        content: { blocks }
      });
      setSuccess('Page saved successfully!');
      loadPages();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  const createPage = async (e) => {
    e.preventDefault();
    if (!newPage.title || !newPage.slug) return;
    setIsSaving(true);
    try {
      await api.createCMSPage(newPage.title, newPage.slug, { blocks: [] });
      setSuccess('Page created successfully!');
      setShowCreateModal(false);
      setNewPage({ title: '', slug: '' });
      loadPages();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Create page error:', err);
      setError(err.message || 'Failed to create page');
    } finally {
      setIsSaving(false);
    }
  };

  const openVisualEditor = (page) => {
    // Initialize blocks if not present
    const blocks = page.content?.blocks || [];
    setVisualEditorPage({ ...page, content: { blocks } });
  };

  // Visual Editor is open
  if (visualEditorPage) {
    return (
      <VisualCMSEditor
        blocks={visualEditorPage.content?.blocks || []}
        onChange={(blocks) => {
          setVisualEditorPage(prev => ({ ...prev, content: { blocks } }));
          saveVisualEditor(blocks);
        }}
        onClose={() => setVisualEditorPage(null)}
        pageTitle={visualEditorPage.title}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-pages">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">CMS Pages</h1>
          <p className="text-neutral-400">Manage website content and pages</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors"
          data-testid="new-page-btn"
        >
          <Plus className="w-4 h-4" />
          NEW PAGE
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Search */}
      <div className="bg-neutral-800 rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
          <input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
      </div>

      {/* Pages List */}
      <div className="bg-neutral-800 rounded-xl border border-neutral-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">Page</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider">Last Updated</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-neutral-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredPages.length > 0 ? (
                filteredPages.map((page) => (
                  <tr key={page.page_id || page.slug} className="hover:bg-neutral-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#3B82F6]" />
                        </div>
                        <span className="font-medium text-white">{page.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-neutral-400">/{page.slug}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        page.status === 'published' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {page.status || 'published'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-neutral-400 text-sm">
                      {page.updated_at ? new Date(page.updated_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a 
                          href={`/${page.slug === 'home' ? '' : page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-neutral-600 rounded-lg transition-colors"
                          title="View Page"
                        >
                          <Eye className="w-4 h-4 text-neutral-400" />
                        </a>
                        <button 
                          onClick={() => openVisualEditor(page)}
                          className="p-2 hover:bg-[#3B82F6]/20 rounded-lg transition-colors"
                          title="Visual Editor"
                        >
                          <Layout className="w-4 h-4 text-[#3B82F6]" />
                        </button>
                        <button 
                          onClick={() => setEditingPage(page)}
                          className="p-2 hover:bg-neutral-600 rounded-lg transition-colors"
                          title="Edit JSON"
                        >
                          <Edit className="w-4 h-4 text-neutral-400" />
                        </button>
                        <button 
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-neutral-400">
                    No pages found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Page Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-md w-full border border-neutral-700">
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h3 className="text-xl font-heading font-bold text-white">Create New Page</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <form onSubmit={createPage} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Page Title</label>
                <input
                  type="text"
                  value={newPage.title}
                  onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                  placeholder="e.g., About Us"
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">URL Slug</label>
                <input
                  type="text"
                  value={newPage.slug}
                  onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="e.g., about-us"
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  required
                />
                <p className="text-xs text-neutral-500 mt-1">This will be the URL: /{newPage.slug || 'page-slug'}</p>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-6 py-3 border border-neutral-600 text-neutral-300 rounded-lg hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-6 py-3 bg-[#3B82F6] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSaving ? 'Creating...' : 'Create Page'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* JSON Edit Modal (fallback) */}
      {editingPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-neutral-700">
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h3 className="text-xl font-heading font-bold text-white">Edit: {editingPage.title}</h3>
              <button onClick={() => setEditingPage(null)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Page Title</label>
                <input
                  type="text"
                  value={editingPage.title}
                  onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Slug</label>
                <input
                  type="text"
                  value={editingPage.slug}
                  disabled
                  className="w-full px-4 py-3 bg-neutral-700/50 border border-neutral-600 rounded-lg text-neutral-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Featured Image URL</label>
                <input
                  type="text"
                  value={editingPage.featured_image || ''}
                  onChange={(e) => setEditingPage({...editingPage, featured_image: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Content (JSON)</label>
                <textarea
                  value={JSON.stringify(editingPage.content || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const content = JSON.parse(e.target.value);
                      setEditingPage({...editingPage, content});
                    } catch {}
                  }}
                  rows={10}
                  className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-[#3B82F6] resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-4 p-6 border-t border-neutral-700">
              <button
                onClick={() => setEditingPage(null)}
                className="px-6 py-3 border border-neutral-600 text-neutral-300 rounded-lg hover:bg-neutral-700"
              >
                Cancel
              </button>
              <button
                onClick={savePage}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
